import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Badge,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import withRouter from "Common/withRouter";
import { createSelector } from "reselect";
import { resetProfileFlag } from "slices/thunk";
import { fetchUserProfile } from "slices/auth/profile/thunk";
import userDummyImg from "assets/images/users/32/user-dummy-img.jpg";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { 
  RiUserLine, 
  RiMailLine, 
  RiPhoneLine, 
  RiMapPinLine, 
  RiBuildingLine,
  RiTeamLine,
  RiBriefcaseLine,
  RiUserStarLine,
  RiCalendarLine,
} from "react-icons/ri";

const UserProfile = () => {
  document.title = "User Profile | Inventory And Asset Management";

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const selectProfile = createSelector(
    (state: any) => state.Profile,
    (profile) => ({
      userProfile: profile.userProfile,
      success: profile.success,
      error: profile.error,
      loading: profile.loading,
    })
  );

  const { userProfile, success, error, loading: profileLoading } = useSelector(selectProfile);

  // Get user ID from localStorage
  useEffect(() => {
    const storedAuthUser = localStorage.getItem("authUser");
    if (storedAuthUser) {
      try {
        const authUser = JSON.parse(storedAuthUser);
        const userId = authUser?.user_id;
        if (userId) {
          dispatch(fetchUserProfile(userId));
        } else {
          setLoading(false);
          toast.error("User ID not found. Please login again.");
        }
      } catch (err) {
        setLoading(false);
        toast.error("Failed to parse user data. Please login again.");
      }
    } else {
      setLoading(false);
      toast.error("Please login to view your profile.");
    }
  }, [dispatch]);

  // Update local loading state when profile data is loaded or error occurs
  useEffect(() => {
    if (userProfile) {
      setLoading(false);
    } else if (error && !profileLoading) {
      setLoading(false);
    }
  }, [userProfile, error, profileLoading]);

  // Timeout fallback - stop loading after 10 seconds if no response
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !userProfile && !error) {
        setLoading(false);
        toast.error("Request timeout. Please try again.");
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading, userProfile, error]);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        dispatch(resetProfileFlag());
      }, 3000);
    }
  }, [dispatch, success]);

  const formatDate = (dateString: string | Date) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const InfoCard = ({ icon: Icon, label, value, colSize = 6 }: any) => (
    <Col md={colSize} className="mb-2">
      <Card className="h-100 border-0 shadow-sm" style={{ transition: "all 0.3s ease" }}>
        <Card.Body className="p-2">
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0 me-2">
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{
                  width: "40px",
                  height: "40px",
                  background: "linear-gradient(135deg, #3969E7 0%, #2a52cf 100%)",
                  color: "#fff",
                }}
              >
                <Icon size={18} />
              </div>
            </div>
            <div className="flex-grow-1">
              <p className="text-muted mb-0" style={{ fontSize: "0.7rem", fontWeight: 500 }}>
                {label}
              </p>
              <h6 className="mb-0 mt-1" style={{ fontSize: "0.85rem", fontWeight: 600, color: "#212529" }}>
                {value || "N/A"}
              </h6>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );

  if (loading || profileLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading profile...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert variant="danger">
            {error || "Failed to load user profile. Please try again."}
          </Alert>
        </Container>
      </div>
    );
  }

  const fullName = userProfile.first_name || userProfile.last_name
    ? `${userProfile.first_name || ""} ${userProfile.last_name || ""}`.trim()
    : userProfile.username || "User";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid style={{ maxWidth: "1400px" }}>
          <FormHeader
            title="User Profile"
            firstLabel="Dashboard"
            secondLabel="User Profile"
            handleArrowClick={() => navigate("/dashboard")}
            handleBreadcrumbClick={() => navigate("/dashboard")}
          />

          {/* Success/Error Messages */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => dispatch(resetProfileFlag())} className="mb-3">
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => dispatch(resetProfileFlag())} className="mb-3">
              {success}
            </Alert>
          )}

          <Row>
            {/* Profile Header Card */}
            <Col lg="12" className="mb-3">
              <Card
                className="border-0 shadow-sm overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #3969E7 0%, #2a52cf 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col md="auto" className="text-center mb-2 mb-md-0">
                      <div className="position-relative d-inline-block">
                        <img
                          src={userDummyImg}
                          alt={fullName}
                          className="rounded-circle"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            border: "4px solid rgba(255,255,255,0.3)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          }}
                        />
                        <Badge
                          bg={userProfile.is_active ? "success" : "secondary"}
                          className="position-absolute bottom-0 end-0 rounded-circle"
                          style={{
                            width: "22px",
                            height: "22px",
                            border: "3px solid #fff",
                          }}
                          title={userProfile.is_active ? "Active" : "Inactive"}
                        />
                      </div>
                    </Col>
                    <Col md className="text-center text-md-start text-white">
                      <h4 className="mb-1" style={{ fontWeight: 700, color: "#fff" }}>
                        {fullName}
                      </h4>
                      <p className="mb-2" style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.9)" }}>
                        <RiUserStarLine className="me-1" />
                        {userProfile.user_type?.user_type || "N/A"}
                      </p>
                      <div className="d-flex flex-wrap justify-content-center justify-content-md-start gap-2">
                        {userProfile.email && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="px-2 py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <RiMailLine className="me-1" size={14} />
                            {userProfile.email}
                          </Badge>
                        )}
                        {userProfile.mobile_number && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="px-2 py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <RiPhoneLine className="me-1" size={14} />
                            {userProfile.mobile_number}
                          </Badge>
                        )}
                        {userProfile.designation && (
                          <Badge
                            bg="light"
                            text="dark"
                            className="px-2 py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <RiBriefcaseLine className="me-1" size={14} />
                            {userProfile.designation}
                          </Badge>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* User Details Section */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm mb-3">
                <Card.Header
                  className="bg-white border-bottom-0"
                  style={{
                    padding: "0.75rem 1rem",
                    background: "linear-gradient(135deg, rgba(57, 105, 231, 0.08) 0%, rgba(57, 105, 231, 0.03) 100%)",
                  }}
                >
                  <h6 className="mb-0" style={{ fontWeight: 700, color: "#3969E7", fontSize: "0.95rem" }}>
                    Personal Information
                  </h6>
                </Card.Header>
                <Card.Body style={{ padding: "1rem" }}>
                  <Row>
                    <InfoCard
                      icon={RiUserLine}
                      label="Username"
                      value={userProfile.username}
                    />
                    <InfoCard
                      icon={RiMailLine}
                      label="Email"
                      value={userProfile.email}
                    />
                    <InfoCard
                      icon={RiPhoneLine}
                      label="Mobile Number"
                      value={userProfile.mobile_number}
                    />
                    <InfoCard
                      icon={RiBriefcaseLine}
                      label="Designation"
                      value={userProfile.designation}
                    />
                    <InfoCard
                      icon={RiUserStarLine}
                      label="User Type"
                      value={userProfile.user_type?.user_type}
                    />
                    <InfoCard
                      icon={RiCalendarLine}
                      label="Status"
                      value={
                        <Badge bg={userProfile.is_active ? "success" : "secondary"} style={{ fontSize: "0.75rem" }}>
                          {userProfile.is_active ? "Active" : "Inactive"}
                        </Badge>
                      }
                    />
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Organization Details */}
          {/* {(userProfile.organization ||
            userProfile.department ||
            userProfile.team) && (
            <Row>
              <Col lg="12">
                <Card className="border-0 shadow-sm mb-3">
                  <Card.Header
                    className="bg-white border-bottom-0"
                    style={{
                      padding: "0.75rem 1rem",
                      background: "linear-gradient(135deg, rgba(57, 105, 231, 0.08) 0%, rgba(57, 105, 231, 0.03) 100%)",
                    }}
                  >
                    <h6 className="mb-0" style={{ fontWeight: 700, color: "#3969E7", fontSize: "0.95rem" }}>
                      Organization Details
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ padding: "1rem" }}>
                    <Row>
                      {userProfile.organization && (
                        <InfoCard
                          icon={RiBuildingLine}
                          label="Organization"
                          value={userProfile.organization?.organization_name}
                        />
                      )}
                      {userProfile.department && (
                        <InfoCard
                          icon={RiBuildingLine}
                          label="Department"
                          value={userProfile.department?.department_name}
                        />
                      )}
                      {userProfile.team && (
                        <InfoCard
                          icon={RiTeamLine}
                          label="Team"
                          value={userProfile.team?.team_name}
                        />
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )} */}

          {/* Address Information */}
          {(userProfile.address ||
            userProfile.city ||
            userProfile.state ||
            userProfile.country ||
            userProfile.pincode) && (
            <Row>
              <Col lg="12">
                <Card className="border-0 shadow-sm mb-3">
                  <Card.Header
                    className="bg-white border-bottom-0"
                    style={{
                      padding: "0.75rem 1rem",
                      background: "linear-gradient(135deg, rgba(57, 105, 231, 0.08) 0%, rgba(57, 105, 231, 0.03) 100%)",
                    }}
                  >
                    <h6 className="mb-0" style={{ fontWeight: 700, color: "#3969E7", fontSize: "0.95rem" }}>
                      Address Information
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ padding: "1rem" }}>
                    <Row>
                      {userProfile.address && (
                        <InfoCard
                          icon={RiMapPinLine}
                          label="Address"
                          value={userProfile.address}
                          colSize={12}
                        />
                      )}
                      <InfoCard
                        icon={RiMapPinLine}
                        label="City"
                        value={userProfile.city}
                      />
                      <InfoCard
                        icon={RiMapPinLine}
                        label="State"
                        value={userProfile.state}
                      />
                      <InfoCard
                        icon={RiMapPinLine}
                        label="Country"
                        value={userProfile.country}
                      />
                      <InfoCard
                        icon={RiMapPinLine}
                        label="Pincode"
                        value={userProfile.pincode}
                      />
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Account Information */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm mb-3">
                <Card.Header
                  className="bg-white border-bottom-0"
                  style={{ 
                    padding: "0.75rem 1rem",
                    background: "linear-gradient(135deg, rgba(57, 105, 231, 0.08) 0%, rgba(57, 105, 231, 0.03) 100%)",
                  }}
                >
                  <h6 className="mb-0" style={{ fontWeight: 700, color: "#3969E7", fontSize: "0.95rem" }}>
                    Account Information
                  </h6>
                </Card.Header>
                <Card.Body style={{ padding: "1rem" }}>
                  <Row>
                    <InfoCard
                      icon={RiCalendarLine}
                      label="Created At"
                      value={formatDate(userProfile.created_at)}
                    />
                    <InfoCard
                      icon={RiCalendarLine}
                      label="Last Updated"
                      value={formatDate(userProfile.updated_at)}
                    />
                    {userProfile.created_by_user && (
                      <InfoCard
                        icon={RiUserLine}
                        label="Created By"
                        value={
                          userProfile.created_by_user.first_name &&
                          userProfile.created_by_user.last_name
                            ? `${userProfile.created_by_user.first_name} ${userProfile.created_by_user.last_name}`
                            : userProfile.created_by_user.username
                        }
                      />
                    )}
                    {userProfile.updated_by_user && (
                      <InfoCard
                        icon={RiUserLine}
                        label="Updated By"
                        value={
                          userProfile.updated_by_user.first_name &&
                          userProfile.updated_by_user.last_name
                            ? `${userProfile.updated_by_user.first_name} ${userProfile.updated_by_user.last_name}`
                            : userProfile.updated_by_user.username
                        }
                      />
                    )}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
